import React from "react";
import { Link } from "react-router-dom";
import { NAV } from "@/nav";
import { ArrowUpRight } from "lucide-react";

const HOME_IMG =
  "https://images.unsplash.com/photo-1758613653735-9d7e87996110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwZmFzaGlvbiUyMG1vZGVscyUyMGVkaXRvcmlhbCUyMHN0dWRpb3xlbnwwfHx8fDE3ODczMjAxMjB8MA&ixlib=rb-4.1.0&q=85";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-12 gap-8 lg:gap-12">
          <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-6 animate-fade-up">
              An independent guide, not an agency
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink mb-6 animate-fade-up" style={{ animationDelay: "80ms" }}>
              A calm starting point
              <br /> for new face models —
              <br /> <em className="italic text-stone">and the people who love them.</em>
            </h1>
            <p className="text-lg text-stone max-w-xl leading-relaxed mb-10 animate-fade-up" style={{ animationDelay: "180ms" }}>
              Clear, honest guidance about entering the modelling industry safely. No hype,
              no sales pitch, no jargon — the sort of briefing a friend who's been through it
              might give you, over a cup of tea.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "260ms" }}>
              <Link
                to="/agency-legitimacy"
                data-testid="home-cta-legitimacy"
                className="btn-terracotta px-7 py-3.5 rounded-full text-sm font-medium tracking-wide inline-flex items-center gap-2"
              >
                Is this agency legitimate?
                <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/safeguarding-contacts"
                data-testid="home-cta-safeguarding"
                className="btn-outline-ink px-7 py-3.5 rounded-full text-sm font-medium tracking-wide"
              >
                If something feels wrong
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 relative">
            <div className="aspect-[4/5] w-full overflow-hidden bg-cream border border-line">
              <img
                src={HOME_IMG}
                alt="A model in a bright studio, looking calm and considered"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div className="hidden lg:block absolute -bottom-6 -left-6 w-40 bg-paper border border-line p-4">
              <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-2">For</p>
              <p className="font-serif text-lg leading-snug text-ink">Models &amp; the parents guiding them.</p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO PROSE */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-12 gap-8 lg:gap-16">
          <div className="col-span-12 lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-4">What this site is</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-ink leading-tight">Written for two people at the kitchen table.</h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6 editorial-body text-[17px]">
            <p>
              You were probably scouted somewhere ordinary — a train station, a market, a coffee shop.
              A card was handed over. Someone at home said, <em>let's look into it properly before we sign anything.</em>
              That's exactly what this site is for.
            </p>
            <p>
              We wrote it for two people: the young model figuring out whether this is real, and the
              parent quietly reading over their shoulder wanting to protect them. You'll find plain-language
              guidance on how the industry actually works — contracts, money, castings, sets, visas, safety —
              with the promotional language stripped out.
            </p>
            <p>
              Nothing here is legal, financial, or immigration advice. Always confirm the specifics with a
              qualified professional. What you'll find here is the honest context that makes the specifics
              easier to understand.
            </p>
          </div>
        </div>
      </section>

      {/* CLUSTERS */}
      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="mb-12 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-4">Where to start</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-ink leading-tight">
              Six clusters, ordered by what usually keeps people up at night.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {NAV.map((cluster, i) => (
              <Link
                key={cluster.key}
                to={`/${cluster.items[0].slug}`}
                data-testid={`home-cluster-${cluster.key}`}
                className="group bg-paper p-8 lg:p-10 flex flex-col justify-between min-h-[220px] transition-colors hover:bg-cream"
              >
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-4">
                    0{i + 1}
                  </p>
                  <h3 className="font-serif text-2xl text-ink leading-snug mb-3">{cluster.label}</h3>
                  <p className="text-sm text-stone leading-relaxed">{cluster.intro}</p>
                </div>
                <p className="mt-6 text-sm text-ink inline-flex items-center gap-1.5 group-hover:text-terracotta transition-colors">
                  Open {cluster.label.toLowerCase()}
                  <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LOLA'S MUM NOTE */}
      <section className="bg-cream border-t border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3 text-center">
            <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-4">A promise</p>
            <p className="font-serif text-2xl lg:text-3xl text-ink leading-snug">
              Every page on this site is written to pass one test:
              <em className="italic text-stone"> would a cautious, non-industry parent find it clear, credible,
              and reassuring within thirty seconds?</em> If a page ever stops doing that, we rewrite it.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
